import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { UsageSummary } from '@joker/contracts'
import { useTRPC } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, ViewTitle, useTooltip, fmtShort } from '../ui'

type SortKey = 'name' | 'sessions' | 'blocks' | 'etests' | 'lastSessionAt' | 'medianMinutes' | 'change'


/**
 * What people actually do in the app.
 *
 * Counted over the patients on the Patients screen only — the LOADTEST fixtures
 * have tens of thousands of sessions between them, and including them would make
 * every figure here a statement about a load generator rather than about
 * anybody's eyes.
 */

const W = 760
const H = 300
const ML = 46
const MR = 18
const MT = 30
const MB = 52
const GAP = 14

/**
 * Where the training population sits in the programme.
 *
 * One bar per segment, counting each patient once, in the segment their most
 * recent session ran under. Anyone who has never trained is absent rather than
 * drawn as a zero — including the people who have taken an eye test and nothing
 * else, because a measurement is not a position in a programme.
 */
function SegmentChart({
  segments,
  bind,
  onOpen,
}: {
  segments: UsageSummary['segments']
  bind: (content: ReactNode) => Record<string, unknown>
  onOpen: (seq: number) => void
}) {
  const plotW = W - ML - MR
  const plotH = H - MT - MB
  const slot = plotW / Math.max(1, segments.length)
  const barW = Math.max(6, slot - GAP)
  const peak = Math.max(1, ...segments.map((s) => s.current))
  /* A round top, so the midpoint tick is a whole number of people. */
  const step = Math.pow(10, Math.floor(Math.log10(Math.max(2, peak))))
  const top = Math.max(2, Math.ceil(peak / step) * step)
  const y = (v: number) => MT + plotH * (1 - v / top)
  const cx = (i: number) => ML + slot * i + slot / 2

  return (
    <div className="chart-wrap">
      <svg
        className="chart"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={segments.map((s) => `${s.label}: ${s.current} patients`).join('. ')}
      >
        {[0, top / 2, top].map((t) => (
          <g key={t}>
            <line
              x1={ML}
              x2={W - MR}
              y1={y(t)}
              y2={y(t)}
              stroke={t === 0 ? 'var(--rule)' : 'var(--grid)'}
              strokeWidth={1}
            />
            <text x={ML - 10} y={y(t) + 4} textAnchor="end" fontSize={11} fill="var(--ink-3)">
              {t}
            </text>
          </g>
        ))}
        {segments.map((sg, i) => (
          <g
            key={sg.seq}
            className="jclick"
            role="button"
            tabIndex={0}
            aria-label={`${sg.label}: ${sg.current} patients. Open the list.`}
            onClick={() => onOpen(sg.seq)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onOpen(sg.seq)}
            {...bind(
              <>
                <b>{sg.label}</b>
                <div className="r">
                  <span>Here now</span>
                  <span>{sg.current}</span>
                </div>
                <div className="r">
                  <span>Ever reached</span>
                  <span>{sg.everReached}</span>
                </div>
                <div className="r">
                  <span>Sessions</span>
                  <span>{sg.sessions.toLocaleString()}</span>
                </div>
              </>,
            )}
          >
            <rect x={ML + slot * i} y={MT} width={slot} height={plotH} fill="transparent" />
            {sg.current > 0 && (
              <>
                <rect
                  x={cx(i) - barW / 2}
                  y={y(sg.current)}
                  width={barW}
                  height={Math.max(2, y(0) - y(sg.current))}
                  rx={4}
                  fill="var(--s1)"
                />
                <text
                  x={cx(i)}
                  y={y(sg.current) - 8}
                  textAnchor="middle"
                  fontSize={11.5}
                  fontWeight={600}
                  fill="var(--ink-2)"
                >
                  {sg.current}
                </text>
              </>
            )}
            {/* Two lines: the numbered segments are short, "Maintenance mix"
                and "Not recorded" are not, and one long label rotated would
                make eleven of them unreadable. */}
            {sg.label.split(' ').reduce<string[][]>(
              (lines, word) => {
                const last = lines[lines.length - 1]!
                if (last.join(' ').length + word.length > 9) lines.push([word])
                else last.push(word)
                return lines
              },
              [[]],
            ).map((line, li) => (
              <text
                key={li}
                x={cx(i)}
                y={H - MB + 20 + li * 13}
                textAnchor="middle"
                fontSize={11}
                fill="var(--ink-3)"
              >
                {line.join(' ')}
              </text>
            ))}
          </g>
        ))}
      </svg>
    </div>
  )
}

type TrainedWindow = 'ever' | 'm6' | 'm3' | 'm1' | 'w2' | 'w1' | 'today' | 'yesterday'

export function Usage({
  onOpenPerson,
  onOpenSegment,
  onOpenTrainedWithin,
}: {
  onOpenPerson: (id: string) => void
  onOpenSegment: (seq: number) => void
  onOpenTrainedWithin: (id: TrainedWindow, paidOnly?: boolean) => void
}) {
  const trpc = useTRPC()
  const q = useQuery(trpc.analytics.usage.queryOptions())
  const { node: tipNode, bind } = useTooltip()
  /* One subject at a time, the way Stats splits its sheets: the programme
     position and the activity figures answer different questions and stacking
     them made the page a scroll. */
  const [sheet, setSheet] = useState<'segments' | 'still' | 'activity'>('segments')
  const [sort, setSort] = useState<SortKey>('sessions')
  const [desc, setDesc] = useState(true)
  const [term, setTerm] = useState('')
  /* Which population the activity windows describe. A filter on one sheet
     rather than a sheet of its own: it is the same question asked of a
     different group. */
  const [who, setWho] = useState<'all' | 'paid'>('all')

  /* Acuity change is derived rather than stored: it is only meaningful for
     somebody with two e-tests, and a column of zeroes for everyone else would
     read as "no improvement" rather than "not measured twice". */
  const change = (p: { firstAcuity: number | null; lastAcuity: number | null; etests: number }) =>
    p.etests > 1 && p.firstAcuity != null && p.lastAcuity != null ? p.lastAcuity - p.firstAcuity : null

  const patients = useMemo(() => {
    const t = term.trim().toLowerCase()
    const list = (q.data?.patients ?? []).filter((p) => !t || p.name.toLowerCase().includes(t))
    const dir = desc ? -1 : 1
    return [...list].sort((a, b) => {
      const pick = (p: (typeof list)[number]) =>
        sort === 'name' ? p.name.toLowerCase() : sort === 'change' ? change(p) : p[sort]
      const x = pick(a)
      const y = pick(b)
      if (x == null && y == null) return 0
      if (x == null) return 1
      if (y == null) return -1
      return x < y ? -dir : x > y ? dir : 0
    })
  }, [q.data, sort, desc, term])

  const th = (k: SortKey, label: string, right = false) => (
    <th
      scope="col"
      className={`${right ? 'num ' : ''}sortable`}
      aria-sort={sort === k ? (desc ? 'descending' : 'ascending') : 'none'}
    >
      <button
        type="button"
        onClick={() => {
          if (sort === k) setDesc(!desc)
          else {
            setSort(k)
            setDesc(k !== 'name')
          }
        }}
      >
        {label}
        {sort === k && <span aria-hidden="true"> {desc ? '↓' : '↑'}</span>}
      </button>
    </th>
  )

  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Loading what="usage" />

  const u = q.data
  const weekPeak = Math.max(1, ...u.weekly.map((w) => w.sessions))
  const acuityPeak = Math.max(1, ...u.acuity.map((a) => a.count))

  const sheetNav = (
    <div className="seg seg-inline" role="tablist" aria-label="Which figures">
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'segments'}
        onClick={() => setSheet('segments')}
      >
        Segments
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'still'}
        onClick={() => setSheet('still')}
      >
        Still training
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={sheet === 'activity'}
        onClick={() => setSheet('activity')}
      >
        Sessions
      </button>
    </div>
  )

  if (sheet === 'segments') {
    const placed = u.segments.reduce((n, sg) => n + sg.current, 0)
    const inProgramme = u.segments
      .filter((sg) => sg.seq >= 1 && sg.seq <= 8)
      .reduce((n, sg) => n + sg.current, 0)
    const maintaining = u.segments
      .filter((sg) => sg.seq >= 9)
      .reduce((n, sg) => n + sg.current, 0)

    return (
      <>
        {sheetNav}
        <ViewTitle suit="diamond">Where patients are in the program</ViewTitle>
        <p className="view-sub">
          Each patient counted once, in the segment their most recent session ran under. Anyone who
          has never trained is left out entirely — including the people who have taken an eye test
          and nothing else, because a measurement is not a position in the program.
        </p>

        <div className="tiles">
          <Tile k="Patients training" v={placed.toLocaleString()} d="at least one session" />
          <Tile k="In a numbered segment" v={inProgramme.toLocaleString()} d="segments 1 to 8" />
          <Tile k="Maintaining" v={maintaining.toLocaleString()} d="past the program" />
        </div>

        <Panel title="Patients by segment" note={`${placed.toLocaleString()} patients`}>
          <SegmentChart segments={u.segments} bind={bind} onOpen={onOpenSegment} />
        </Panel>

        <Panel title="Segment by segment" note="where they are, and where they have been">
          <table className="data">
            <thead>
              <tr>
                <th>Segment</th>
                <th className="num">Here now</th>
                <th className="num">Ever reached</th>
                <th className="num">Sessions</th>
              </tr>
            </thead>
            <tbody>
              {u.segments.map((sg) => (
                <tr
                  key={sg.seq}
                  tabIndex={0}
                  onClick={() => onOpenSegment(sg.seq)}
                  onKeyDown={(e) => e.key === 'Enter' && onOpenSegment(sg.seq)}
                >
                  <td>
                    <strong>{sg.label}</strong>
                  </td>
                  <td className="num">{sg.current || <span className="muted">—</span>}</td>
                  <td className="num">{sg.everReached.toLocaleString()}</td>
                  <td className="num">{sg.sessions.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="footnote">
            The chart draws <strong>here now</strong>, so the bars add up to the patient count.{' '}
            <strong>Ever reached</strong> counts everyone who has trained in a segment whether or
            not they are still in it, so it adds up to more — it is the one to read as a funnel.
            &ldquo;No segment&rdquo; is Oryx&rsquo;s own sequence 0: sessions run outside a
            segmented program, running from 2021 to today, so it is the pre-segment population
            rather than a first step. &ldquo;Not recorded&rdquo; is sessions that carry no segment
            at all.
          </p>
        </Panel>
        {tipNode}
      </>
    )
  }

  if (sheet === 'still') {
    const rows = who === 'paid' ? u.recencyPaid : u.recency
    return (
      <>
        {sheetNav}
        <ViewTitle suit="diamond">Who is still training</ViewTitle>
        <p className="view-sub">
          Everyone who has ever trained, then the same population at narrowing windows. Each row is
          a subset of the one above it and every percentage is of the all-time figure, so reading
          down the list is reading attrition rather than comparing unrelated ratios. Yesterday sits
          at the end as a day of its own rather than a narrower window, which is why it carries no
          drop-off. Counted from the sessions themselves, so &ldquo;today&rdquo; means today.
        </p>

        <div className="seg seg-inline" role="tablist" aria-label="Which patients">
          <button type="button" role="tab" aria-selected={who === 'all'} onClick={() => setWho('all')}>
            All patients
          </button>
          <button type="button" role="tab" aria-selected={who === 'paid'} onClick={() => setWho('paid')}>
            Paying only
          </button>
        </div>

        {/* Tiles rather than bars: every row here is a headline figure in its
            own right, and a bar chart whose first row is always 100% spends
            most of its width saying so. */}
        {/* Four across, so eight tiles fill two rows exactly — auto-fit left a
            stripe of empty grid on the second row. */}
        <div className="tiles tiles-4">
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className="tile tile-btn"
              onClick={() => onOpenTrainedWithin(r.id as TrainedWindow, who === 'paid')}
              aria-label={`${r.label}: ${r.count} patients. Open the list.`}
            >
              <span className="k">{r.label}</span>
              <span className="v num">{r.count.toLocaleString()}</span>
              <span className="d">{r.id === 'ever' ? 'all time' : `${r.pct}% of all time`}</span>
            </button>
          ))}
        </div>

        <Panel title="Row by row" note={who === 'paid' ? 'paying patients only' : 'all patients'}>
          <table className="data">
            <thead>
              <tr>
                <th>Trained</th>
                <th className="num">Patients</th>
                <th className="num">Share of all-time</th>
                <th className="num">Lost since the row above</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                /* No drop-off for a row that is not a subset of the one above
                   it: yesterday is not a narrower window than today. */
                const prev = i === 0 || !r.nested ? null : rows[i - 1]
                const open = () => onOpenTrainedWithin(r.id as TrainedWindow, who === 'paid')
                return (
                  /* The same control as the tile above it, so a row of this
                     table and the tile for the same window open the same list
                     — the toggle travels with both. */
                  <tr
                    key={r.id}
                    className="pickable"
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={(e) => e.key === 'Enter' && open()}
                    aria-label={`${r.label}: ${r.count} patients. Open the list.`}
                  >
                    <td>
                      <strong>{r.label}</strong>
                    </td>
                    <td className="num">{r.count.toLocaleString()}</td>
                    <td className="num">{r.pct}%</td>
                    <td className="num">
                      {prev ? (prev.count - r.count).toLocaleString() : <span className="muted">—</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <p className="footnote">
            Click a row to see the patients in it. The All / Paying toggle travels with it, so the
            list matches the figure that was pressed rather than a wider one.
          </p>
        </Panel>
        {tipNode}
      </>
    )
  }

  return (
    <>
      {sheetNav}
      <ViewTitle suit="diamond">How the product is used</ViewTitle>
      <p className="view-sub">
        Every training session, the exercise blocks inside it, and the eye tests taken between
        segments. Most sessions run four blocks — a threshold task, two lateral-masking blocks and a
        combined one — and the threshold each one measures is what the program exists to move.
      </p>

      <div className="tiles">
        <Tile k="Sessions" v={u.sessions.toLocaleString()} d={`${u.sessionPatients} patients`} />
        <Tile
          k="Median session"
          v={u.medianSessionMinutes == null ? '—' : `${u.medianSessionMinutes} min`}
          d="start to finish"
        />
        <Tile
          k="Exercise blocks"
          v={u.blocks.toLocaleString()}
          d={`across ${u.sessionsWithBlocks.toLocaleString()} of ${u.sessions.toLocaleString()} sessions`}
        />
        <Tile k="Eye tests" v={u.etests.toLocaleString()} d={`${u.etestPatients} patients`} />
      </div>

      <Panel title="Sessions per week" note="last eight weeks">
        <div className="bars">
          {u.weekly.map((w) => (
            <div
              key={w.week}
              className="bar-row"
              {...bind(
                <>
                  <b>Week of {new Date(w.week).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</b>
                  <div className="r">
                    <span>Sessions</span>
                    <span>{w.sessions}</span>
                  </div>
                  <div className="r">
                    <span>People</span>
                    <span>{w.people}</span>
                  </div>
                </>,
              )}
            >
              <span className="bar-label">
                {new Date(w.week).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </span>
              <span className="bar-track">
                <span className="bar" style={{ width: `${Math.max(1, (w.sessions / weekPeak) * 100)}%` }} />
              </span>
              <span className="bar-value num">{w.sessions}</span>
            </div>
          ))}
        </div>
      </Panel>

      <div className="grid-2">
        <Panel title="Exercise blocks" note="by kind">
          {u.blockKinds.length === 0 ? (
            <p className="footnote">No block results yet.</p>
          ) : (
            <table className="stat-table">
              <thead>
                <tr>
                  <th scope="col">Block</th>
                  <th scope="col" className="r">Runs</th>
                  <th scope="col" className="r">Median threshold</th>
                  <th scope="col" className="r">Accuracy</th>
                </tr>
              </thead>
              <tbody>
                {u.blockKinds.map((b) => (
                  <tr key={b.name}>
                    <th scope="row">{b.name}</th>
                    <td className="r num">{b.blocks.toLocaleString()}</td>
                    <td className="r num">{b.medianThreshold ?? '—'}</td>
                    <td className="r num">{b.accuracy == null ? '—' : `${b.accuracy}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="footnote">
            Accuracy is right answers over all answers. The threshold is the staircase value the
            block settled on. The {(u.sessions - u.sessionsWithBlocks).toLocaleString()} sessions
            missing from this count are not abandoned ones — they belong to 21 long-standing
            patients and average seventeen minutes each; the dashboard simply no longer serves
            their result files.
          </p>
        </Panel>

        <Panel title="Acuity measured" note={`${u.etests.toLocaleString()} eye tests`}>
          <div className="bars">
            {u.acuity.map((a) => (
              <div key={a.label} className="bar-row">
                <span className="bar-label">{a.label}</span>
                <span className="bar-track">
                  <span className="bar" style={{ width: `${Math.max(1, (a.count / acuityPeak) * 100)}%` }} />
                </span>
                <span className="bar-value num">{a.count}</span>
              </div>
            ))}
          </div>
          <p className="footnote">
            Decimal acuity from the e-tests, every measurement rather than one per patient — somebody
            tested ten times appears ten times, which is what makes the spread a distribution of
            measurements and not of people.
          </p>
        </Panel>
      </div>

      <Panel title="Who is training" note={`${patients.length.toLocaleString()} patients`}>
        <div className="toolbar">
          <input
            type="search"
            className="search"
            placeholder="Search patients"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
        <table className="data">
          <thead>
            <tr>
              {th('name', 'Patient')}
              {th('sessions', 'Sessions', true)}
              {th('blocks', 'Blocks', true)}
              {th('medianMinutes', 'Median', true)}
              {th('etests', 'Eye tests', true)}
              {th('change', 'Acuity', true)}
              {th('lastSessionAt', 'Last session', true)}
            </tr>
          </thead>
          <tbody>
            {patients.map((p) => {
              const d = change(p)
              return (
                <tr
                  key={p.id}
                  tabIndex={0}
                  onClick={() => onOpenPerson(p.id)}
                  onKeyDown={(e) => e.key === 'Enter' && onOpenPerson(p.id)}
                >
                  <td>
                    <strong>{p.name}</strong>
                  </td>
                  <td className="num">{p.sessions.toLocaleString()}</td>
                  <td className="num">{p.blocks ? p.blocks.toLocaleString() : <span className="muted">—</span>}</td>
                  <td className="num">{p.medianMinutes == null ? '—' : `${p.medianMinutes}m`}</td>
                  <td className="num">{p.etests || <span className="muted">—</span>}</td>
                  <td className="num">
                    {d == null ? (
                      <span className="muted">
                        {p.lastAcuity == null ? '—' : p.lastAcuity.toFixed(2)}
                      </span>
                    ) : (
                      <>
                        {p.firstAcuity!.toFixed(2)} → {p.lastAcuity!.toFixed(2)}
                      </>
                    )}
                  </td>
                  <td className="num">{fmtShort(p.lastSessionAt)}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <p className="footnote">
          Acuity shows first and last measurement for anyone tested more than once; a single
          measurement is shown on its own, because one reading is a starting point and not a result.
          Open a patient for the session-by-session detail.
        </p>
      </Panel>

      <Panel title="App versions" note="sessions by build">
        <div className="bars">
          {u.appVersions.map((v) => (
            <div key={v.version} className="bar-row">
              <span className="bar-label">{v.version}</span>
              <span className="bar-track">
                <span
                  className="bar"
                  style={{ width: `${Math.max(1, (v.sessions / Math.max(1, u.appVersions[0]!.sessions)) * 100)}%` }}
                />
              </span>
              <span className="bar-value num">{v.sessions.toLocaleString()}</span>
            </div>
          ))}
        </div>
      </Panel>
      {tipNode}
    </>
  )
}
